import { Message } from "@arco-design/web-react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isGenericHttpFailureMessage, isHttpClientError } from "../../../lib/http/errors";
import type { GeoBusinessVerifyResult, GeoOperationStatus } from "../../../shared/business-geo";

import { dicStayAreaDefaultListParams } from "../config";
import {
  createDicStayArea,
  fetchDicStayArea,
  fetchDicStayAreaList,
  resolveGeoArea,
  updateDicStayArea,
} from "../repo/dicStayAreaRepo";
import {
  applyGeoResolveResultToMutation,
  buildGeoResolveAreaName,
  normalizeDicStayAreaFilters,
} from "../service/dicStayAreaService";
import type {
  DicStayAreaGeoResolveRequest,
  DicStayAreaItem,
  DicStayAreaListFilters,
  DicStayAreaMutation,
} from "../types";

const dicStayAreaQueryRootKey = ["dic_stay_area", "list"] as const;

export const emptyDicStayAreaMutation: DicStayAreaMutation = {
  stayAreaName: "",
  spellCode: "",
  areaLevel: "area",
  province: "",
  city: "",
  district: "",
  township: "",
  navigationAddress: "",
  lng: undefined,
  lat: undefined,
  isDisabled: false,
  isAiCreated: false,
  remark: "",
};

/** 管理住宿点列表页查询、分页与编辑行为。 */
export function useDicStayAreaRuntime(deepLinkStayAreaId?: string) {
  const queryClient = useQueryClient();
  const [draftFilters, setDraftFilters] = useState<DicStayAreaListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<DicStayAreaListFilters>({});
  const [page, setPage] = useState(dicStayAreaDefaultListParams.page);
  const [pageSize, setPageSize] = useState(dicStayAreaDefaultListParams.pageSize);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [geoFeedbackMessage, setGeoFeedbackMessage] = useState("");
  const [geoResolving, setGeoResolving] = useState(false);
  const [geoStatus, setGeoStatus] = useState<GeoOperationStatus>("idle");
  const [geoResult, setGeoResult] = useState<GeoBusinessVerifyResult>();

  const listParams = useMemo(
    () => ({
      ...dicStayAreaDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );

  const query = useQuery({
    queryKey: [...dicStayAreaQueryRootKey, listParams],
    queryFn: () => fetchDicStayAreaList(listParams),
  });
  const deepLinkedItemQuery = useQuery({
    queryKey: ["dic_stay_area", "detail", deepLinkStayAreaId],
    queryFn: () => fetchDicStayArea(deepLinkStayAreaId as string),
    enabled: Boolean(deepLinkStayAreaId),
  });

  const listPage = query.data ?? {
    items: [],
    page,
    pageSize,
    total: 0,
  };

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: dicStayAreaQueryRootKey,
    });

  const createMutation = useMutation({
    mutationFn: createDicStayArea,
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ stayAreaId, payload }: { stayAreaId: string; payload: DicStayAreaMutation }) =>
      updateDicStayArea(stayAreaId, payload),
    onSuccess: (item) => {
      if (item.vectorSyncWarning) Message.warning({ content: item.vectorSyncWarning, duration: 8000 });
      return invalidateList();
    },
  });
  const geoResolveMutation = useMutation({
    mutationFn: (payload: DicStayAreaGeoResolveRequest) => resolveGeoArea(payload),
  });

  return {
    draftFilters,
    items: listPage.items,
    deepLinkedItem: deepLinkedItemQuery.data,
    loading: query.isLoading,
    page,
    pageSize,
    total: listPage.total,
    errorMessage: query.error ? "住宿点列表加载失败，请稍后重试。" : "",
    mutationErrorMessage,
    geoFeedbackMessage,
    geoResolving,
    geoStatus,
    geoResult,
    setDraftFilters,
    clearMutationError() {
      setMutationErrorMessage("");
    },
    clearGeoFeedback() {
      setGeoFeedbackMessage("");
      setGeoStatus("idle");
      setGeoResult(undefined);
    },
    handleSearch() {
      setSubmittedFilters(normalizeDicStayAreaFilters(draftFilters));
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreateStayArea(payload: DicStayAreaMutation) {
      await runMutation(() => createMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdateStayArea(stayArea: DicStayAreaItem, payload: DicStayAreaMutation) {
      await runMutation(
        () => updateMutation.mutateAsync({ stayAreaId: stayArea.stayAreaId, payload }),
        setMutationErrorMessage,
      );
    },
    async handleResolveGeo(payload: DicStayAreaMutation) {
      setGeoFeedbackMessage("");
      setGeoResolving(true);
      setGeoStatus("processing");
      try {
        const result = await runMutation(
          () =>
            geoResolveMutation.mutateAsync({
              province: payload.province,
              city: payload.city,
              district: payload.district,
              areaLevel: payload.areaLevel,
              township: payload.township,
              sourceAddress: payload.navigationAddress || undefined,
              areaName: buildGeoResolveAreaName(payload),
              scenario: "stay_area",
              contextName: payload.navigationAddress || payload.remark || undefined,
            }),
          setMutationErrorMessage,
        );
        setGeoFeedbackMessage(result.message);
        const panelResult: GeoBusinessVerifyResult = {
          scenario: "stay_area",
          status: result.status ?? (result.needsReview ? "needs_correction" : "confirmed"),
          conclusion: result.message,
          llmConclusion: result.ambiguityNotes,
          amapStandardAddress: result.navigationAddress,
          province: result.province,
          city: result.city,
          district: result.district,
          longitude: result.longitude,
          latitude: result.latitude,
          coordinateSystem: result.coordinateSystem,
          confidenceScore: result.confidenceScore,
          correctionReason: result.needsReview ? result.message : "",
          retryable: result.retryable ?? false,
          candidates: [],
        };
        setGeoResult(panelResult);
        setGeoStatus(panelResult.status);
        return applyGeoResolveResultToMutation(payload, result);
      } catch (error) {
        setGeoStatus("retryable_failure");
        throw error;
      } finally {
        setGeoResolving(false);
      }
    },
  };
}

/** 执行变更请求并统一记录保存失败提示。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    setMutationErrorMessage(resolveDicStayAreaMutationErrorMessage(error));
    throw error;
  }
}

/** 把住宿点保存失败整理成可展示中文，避免 422/业务错误被笼统文案盖住。 */
function resolveDicStayAreaMutationErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    return "保存失败，请检查填写内容后重试。";
  }
  const detail = error.message?.trim() ?? "";
  const hasReadableDetail =
    Boolean(detail)
    && !isGenericHttpFailureMessage(detail)
    && detail !== "dic-stay-area-name-conflict";

  if (error.code === "DIC_STAY_AREA_NAME_CONFLICT") {
    return hasReadableDetail ? detail : "住宿点名称已存在，请勿重复新增。";
  }
  if (error.code === "SEMANTIC_DUPLICATE_CHECK_FAILED" && hasReadableDetail) {
    return detail;
  }
  if (error.statusCode === 422) {
    return hasReadableDetail ? detail : "请完整填写住宿点名称、省份和城市。";
  }
  if (hasReadableDetail) {
    return detail;
  }
  return "保存失败，请检查填写内容后重试。";
}
